import { Pipe, PipeTransform } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { AutomationHelpers } from 'app/automations/automation-helpers';
import { OnsiteCampaignType } from 'app/campaign/campaign.model';
import { WidgetSimpleInfo } from 'app/campaign/widgets.model';
import { LanguageService } from 'app/core/language.service';
import { ActionsPropertiesDetails } from 'app/automations/automation-helpers-actions';
import { Automation } from 'app/automations/automations-models';
import WidgetAutomationHelpers from 'app/campaign/widget-automation-helpers';

@Pipe({
  name: 'generateAutomationNames',
  pure: false,
  standalone: false,
})
export class GenerateAutomationNames implements PipeTransform {
  constructor(
    private translateService: TranslateService,
    private languageService: LanguageService,
  ) {}
  transform(automation: Automation, widgets?: Array<WidgetSimpleInfo>, couponNames?: Map<string, string>) {
    if (!automation) {
      return;
    }

    //Check if it is a onsite campaign automation
    let isOnsiteCampaign = false,
      triggerIndex = -1,
      foundWidget: WidgetSimpleInfo;

    if (
      widgets &&
      automation.triggers &&
      (triggerIndex = automation.triggers.findIndex((t) => t.type === 'WidgetFormSubmission')) !== -1
    ) {
      isOnsiteCampaign = true;
      let widgetIndex = -1;

      if (
        automation.triggers[triggerIndex].argument &&
        (widgetIndex = widgets.findIndex((w) => {
          return +automation.triggers[triggerIndex].argument === w.id;
        })) !== -1
      ) {
        foundWidget = widgets[widgetIndex];
      }
    }

    // We have a name saved in the db, return it (component-action automations use generated names, like form triggers)
    if (automation.name && !automation.isFormTrigger && !AutomationHelpers.isComponentActionAutomation(automation)) {
      if (!isOnsiteCampaign || !foundWidget) {
        const abandonCartName = WidgetAutomationHelpers.translateAbandonCartName(automation, this.translateService);
        return '<b>' + (abandonCartName || automation.name) + '</b>';
      } else {
        if (foundWidget.name) {
          return '<b>' + automation.name + ' (' + foundWidget.name + ')</b>';
        } else if (foundWidget.campaignType !== OnsiteCampaignType.ABTesting) {
          const variantString = this.languageService.translateInstantly(
            'campaign.widget.sidebar.form-settings.gen-automation-variant',
            { variantId: foundWidget.order + 1 },
          );
          return '<b>' + automation.name + ' ' + variantString + '</b>';
        } else {
          const controlOrChallenger =
            foundWidget.order === 0
              ? this.languageService.translateInstantly('campaign.ab-test.control')
              : this.languageService.translateInstantly('campaign.ab-test.challenger');
          const variantString = this.languageService.translateInstantly(
            'campaign.widget.sidebar.form-settings.gen-automation-variant-abtest',
            { abTestVariant: controlOrChallenger },
          );
          return '<b>' + automation.name + ' ' + variantString + '</b>';
        }
      }
    }

    // Generate a new name
    let name = '';

    this.translateService.get('automations.when').subscribe((text) => {
      name += text + ' ';
    });

    let i = 1;
    automation.triggers.forEach((x) => {
      // Trigger name
      this.translateService.get(AutomationHelpers.getTriggerTitleKey(x)).subscribe((text) => {
        name += '<b>' + text + '</b>';
      });

      // Trigger operator
      if (x.operator) {
        this.translateService.get('automations.operators.' + x.operator).subscribe((text) => {
          name += ' ' + text.toLowerCase();
        });
      }

      // Trigger argument, argumentName is only set for triggers that have their id as value in argument
      let argument = x.argument || x.type;
      if (argument) {
        if (AutomationHelpers.isComponentActionTrigger(x) && widgets && widgets.length) {
          argument =
            AutomationHelpers.getComponentActionDisplayName(this.translateService, widgets, automation, x, {
              skipBoldText: true,
            }) || argument;
        } else if (x.type === 'Goals' && widgets && widgets.length) {
          argument =
            AutomationHelpers.prettifyGoalsNames(this.translateService, widgets, [x], 'argument')[0].argument ||
            argument;
        } else if (x.subType) {
          argument = AutomationHelpers.describeEventScope(x, widgets, this.translateService, couponNames) || argument;
        }
        if (widgets) {
          const widget = widgets.find((w) => +argument === w.id);
          if (widget?.campaignName) {
            argument = widget.campaignName;
          }
        }
        name += ' "<b>' + argument + '</b>"';
      }
      // And?
      if (i !== automation.triggers.length) {
        this.translateService.get('automations.and').subscribe((text) => {
          name += ' ' + text + ' ';
        });
      }
      i++;
    });

    // Actions
    this.translateService.get('automations.then').subscribe((text) => {
      name += ' ' + text + ' ';
    });

    i = 1;
    automation.actions.forEach((x) => {
      const translationKey = ActionsPropertiesDetails[x.$type].translationKey;
      this.translateService.get('automations.actions.' + translationKey + '.title').subscribe((text) => {
        name += ' <b>' + text + '</b>';
      });
      // And?
      if (i !== automation.actions.length) {
        this.translateService.get('automations.and').subscribe((text) => {
          name += ' ' + text;
        });
      }

      i++;
    });

    // Remove html tags
    //name = name.replace(/<(.|\n)*?>/g, '');

    return name;
  }
}
